/**
 * TriggerModal — Select a trigger to start a new workflow.
 *
 * ORGANISATION:
 *  Triggers are grouped into 6 categories. Each card shows:
 *    - Icon, title, short description
 *    - "When to use" bullet examples
 *    - Status badge: PREMIUM (when gated)
 *
 * ADDING A NEW TRIGGER:
 *  1. Add its ID to triggerDefs in types.tsx.
 *  2. Add a TriggerDef entry in the CATEGORIES array below.
 *  3. Set live: true only when velaro-messaging actively evaluates the trigger.
 *  4. Document "examples" so admins know when to use it.
 *
 * AUDIT / DIAGNOSTICS:
 *  Selecting a trigger is logged immediately. Subsequent trigger-type changes
 *  inside the editor are logged by TriggerEditor via useActivityLog.
 */
import * as React from "react";
import { useState, useMemo } from "react";
import { Modal, ModalBody, ModalHeader } from "@velaro/velaro-component-lib";

// ── Icon imports ──────────────────────────────────────────────────────────────
import IconMessageChatSquare from "@/icons/IconMessageChatSquare";
import IconMessageCheckSquare from "@/icons/IconMessageCheckSquare";
import IconMessageNotificationSquare from "@/icons/IconMessageNotificationSquare";
import IconOutgoingMessageSquare from "@/icons/IconOutgoingMessageSquare";
import IconUsersPlus from "@/icons/IconUsersPlus";
import IconLink from "@/icons/IconLink";
import IconFileCheck from "@/icons/IconFileCheck";
import IconUpload from "@/icons/IconUpload";
import IconDownload from "@/icons/IconDownload";
import IconAgentTimeout from "@/icons/IconAgentTimeout";
import IconUnresponsiveAgentSquare from "@/icons/IconUnresponsiveAgentSquare";
import IconUnresponsiveVisitorSquare from "@/icons/IconUnresponsiveVisitorSquare";
import IconCalendar from "@/icons/IconCalendar";
import IconTicket from "@/icons/IconTicket";
import IconArrowSquareDownRight from "@/icons/IconArrowSquareDownRight";
import IconArrowRefresh from "@/icons/IconArrowRefresh";
import IconRatings from "@/icons/IconRatings";
import IconEmail from "@/icons/IconEmail";
import IconFlag from "@/icons/IconFlag";
import IconPersonAdd from "@/icons/IconPersonAdd";
import IconPersonTeam from "@/icons/IconPersonTeam";
import IconHistoryTime from "@/icons/IconHistoryTime";
import IconContactsCrm from "@/icons/IconContactsCrm";
import { IconProps } from "@velaro/velaro-component-lib";
import { PremiumTag } from "../../../components/PremiumTag";

// ── Trigger definition type ───────────────────────────────────────────────────
interface TriggerDef {
  id: string;
  icon: (props: IconProps) => React.JSX.Element;
  title: string;
  description: string;
  /** Short "when would you use this" bullet examples shown in the card. */
  examples: string[];
  live?: boolean;
  premium?: boolean;
  category: TriggerCategory;
}

type TriggerCategory =
  | "Pre-conversation"
  | "Conversation"
  | "Visitor & Contact"
  | "Agent & Team"
  | "Engagement & Feedback"
  | "Time & Schedule"
  | "Integrations";

// ── Inline SVG icons for triggers that don't have a dedicated Icon file ───────
function IconLabelTag(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
    </svg>
  );
}

function IconRobot(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <rect x="3" y="8" width="18" height="13" rx="2" />
      <path strokeLinecap="round" d="M12 3v5M9 3h6" />
      <circle cx="8.5" cy="14" r="1.5" fill="currentColor" />
      <circle cx="15.5" cy="14" r="1.5" fill="currentColor" />
      <path strokeLinecap="round" d="M9 18h6" />
    </svg>
  );
}

function IconClock(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3" />
    </svg>
  );
}

function IconWebhook(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
  );
}

function IconShoppingCart(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
    </svg>
  );
}

function IconDeal(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
    </svg>
  );
}

function IconInboundEmail(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 9v.906a2.25 2.25 0 01-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 001.183 1.981l6.478 3.488m8.839-2.51l-4.66 2.51m0 0l-1.023.55a2.25 2.25 0 01-2.134 0l-1.022-.55m0 0l-4.661-2.51m16.5 1.615a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V8.844a2.25 2.25 0 011.183-1.98l7.5-4.04a2.25 2.25 0 012.134 0l7.5 4.04a2.25 2.25 0 011.183 1.98V19.5z" />
    </svg>
  );
}

// ── Trigger definitions ───────────────────────────────────────────────────────
const ALL_TRIGGERS: TriggerDef[] = [
  // ── Pre-conversation ─────────────────────────────────────────────────────
  {
    category: "Pre-conversation",
    id: "widget_loaded",
    icon: IconMessageChatSquare,
    title: "Widget Loads on Page",
    description: "Fires when the chat widget loader runs on a visitor's page — before the chat button appears. Use to decide show/hide the button, or which chat window design to serve (by URL, time, agent availability, etc.).",
    examples: [
      "Hide the chat button on checkout pages",
      "Serve a different chat window design on pricing vs support pages",
      "Show the widget only during business hours",
    ],
    live: true,
  },
  {
    category: "Pre-conversation",
    id: "chat_button_clicked",
    icon: IconMessageChatSquare,
    title: "Chat Button Clicked",
    description: "Fires when a visitor clicks the chat button — before any conversation exists. Use to configure pre-chat survey, branding, or widget config based on visitor context.",
    examples: [
      "Swap the pre-chat survey based on the page URL",
      "Override branding colors for specific campaigns",
      "Decide whether to show a pre-chat form or jump straight into chat",
    ],
    live: true,
  },

  // ── Conversation ──────────────────────────────────────────────────────────
  {
    category: "Conversation",
    id: "conversation_opened",
    icon: IconMessageChatSquare,
    title: "Conversation Opened",
    description: "Fires the moment a new conversation is started — ideal for immediate greetings, intake surveys, or instant bot engagement.",
    examples: [
      "Show a welcome message and capture visitor name/email",
      "Route to the right team based on the page they came from",
      "Launch a pre-chat survey before connecting to an agent",
    ],
    live: true,
  },
  {
    category: "Conversation",
    id: "conversation_resolved",
    icon: IconMessageCheckSquare,
    title: "Conversation Resolved",
    description: "Fires when a conversation is marked resolved or closed. Use to trigger post-conversation automation like CSAT surveys, CRM updates, or follow-up emails.",
    examples: [
      "Push transcript to HubSpot or Dynamics 365",
      "Send a follow-up email 24 hours after resolution",
      "Tag the contact in your CRM with the conversation outcome",
    ],
    live: true,
  },
  {
    category: "Conversation",
    id: "conversation_assigned",
    icon: IconUsersPlus,
    title: "Conversation Assigned",
    description: "Fires when a conversation is assigned to an agent or team. Use to send agent notifications, update CRM assignment records, or log reassignments.",
    examples: [
      "Notify the assigned agent via email or webhook",
      "Update a deal owner in HubSpot when a conversation is routed",
      "Log all reassignments for SLA tracking",
    ],
    live: true,
  },

  // ── Visitor & Contact ─────────────────────────────────────────────────────
  {
    category: "Visitor & Contact",
    id: "incoming_message",
    icon: IconMessageNotificationSquare,
    title: "Incoming Message",
    description: "Fires on every new message received from a visitor mid-conversation. Use for keyword detection, sentiment analysis checks, or real-time escalation.",
    examples: [
      "Detect keywords like 'cancel' or 'refund' and route to retention",
      "Log every message to an external analytics system",
      "Send an auto-acknowledgement if no agent has replied within 60s",
    ],
    live: true,
  },

  // ── Agent & Team ──────────────────────────────────────────────────────────

  // ── Engagement & Feedback ─────────────────────────────────────────────────
  {
    category: "Engagement & Feedback",
    id: "csat_submitted",
    icon: IconRatings,
    title: "CSAT Rating Submitted",
    description: "Fires when a visitor submits a CSAT survey response after a conversation. Filter by rating range — e.g., fire only for 1–2 star ratings to trigger a service recovery workflow.",
    examples: [
      "Rating 1–2 → alert a manager and create a recovery task",
      "Rating 5 → ask the customer for a review or referral",
      "Any rating → push score to HubSpot contact timeline",
    ],
    live: true,
  },
  {
    category: "Engagement & Feedback",
    id: "email_opened",
    icon: IconEmail,
    title: "Campaign Email Opened",
    description: "Fires when a recipient opens one of your broadcast emails. Use to trigger a timely follow-up or personalise the next touchpoint.",
    examples: [
      "Email opened → start a live chat proactive greeting for 24h",
      "Opened but no link clicked after 3 days → send a reminder",
      "Log open events to HubSpot contact engagement timeline",
    ],
    live: true,
  },
  {
    category: "Engagement & Feedback",
    id: "email_link_clicked",
    icon: IconEmail,
    title: "Campaign Link Clicked",
    description: "Fires when a recipient clicks any tracked link in a broadcast email. Use to identify high-intent leads and trigger immediate sales outreach.",
    examples: [
      "Clicked pricing link → immediately notify the sales rep",
      "Clicked demo link but didn't book → send a calendar invite",
      "Route to Sales team if a prospect clicks 3+ links in one email",
    ],
    live: true,
  },
];

// ── Category metadata ─────────────────────────────────────────────────────────
const CATEGORY_ORDER: TriggerCategory[] = [
  "Pre-conversation",
  "Conversation",
  "Visitor & Contact",
  "Agent & Team",
  "Engagement & Feedback",
  "Time & Schedule",
  "Integrations",
];

const CATEGORY_DESCRIPTIONS: Record<TriggerCategory, string> = {
  "Pre-conversation":      "Fires before a conversation exists — widget loader and button clicks",
  "Conversation":          "Fires based on the lifecycle of a conversation",
  "Visitor & Contact":     "Fires based on visitor behavior or contact data changes",
  "Agent & Team":          "Fires when agents or routing conditions are breached",
  "Engagement & Feedback": "Fires based on survey responses, email activity, and file transfers",
  "Time & Schedule":       "Fires on a recurring schedule independent of conversations",
  "Integrations":          "Fires based on events in connected CRM, e-commerce, or external systems",
};

// ── Component ─────────────────────────────────────────────────────────────────
interface Props {
  onClose: () => void;
  onSelect(triggerId: string): void;
}

export default function TriggerModal({ onClose, onSelect }: Props) {
  const [query, setQuery] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [openCategories, setOpenCategories] = useState<Set<TriggerCategory>>(
    new Set(CATEGORY_ORDER)
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return ALL_TRIGGERS;
    const q = query.toLowerCase();
    return ALL_TRIGGERS.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.examples.some((e) => e.toLowerCase().includes(q)) ||
        t.category.toLowerCase().includes(q)
    );
  }, [query]);

  const grouped = useMemo(() => {
    const map = new Map<TriggerCategory, TriggerDef[]>();
    for (const cat of CATEGORY_ORDER) map.set(cat, []);
    for (const t of filtered) map.get(t.category)?.push(t);
    return map;
  }, [filtered]);

  function toggleCategory(cat: TriggerCategory) {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  }

  const liveCount  = ALL_TRIGGERS.filter((t) => t.live).length;
  const totalCount = ALL_TRIGGERS.length;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <Modal maxWidth="max-w-6xl" show={true}>
        <ModalHeader title="Select a Trigger" onClose={onClose} />
        <ModalBody>
          <div className="-mt-3">
            {/* Header copy + stats */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-slate-500 max-w-xl">
                Triggers are events or conditions that kick-start your workflow.
                Choose one below — then build out the actions that follow.
              </p>
              <span className="text-xs text-slate-400 whitespace-nowrap ml-4">
                {liveCount} live &bull; {totalCount} total
              </span>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Search triggers…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search triggers"
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary-hover"
              />
              <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <path strokeLinecap="round" d="M21 21l-4.35-4.35" />
              </svg>
            </div>

            {/* Help: priority + execution order */}
            <div className="mb-3 border border-slate-200 rounded-md bg-slate-50">
              <button
                type="button"
                onClick={() => setHelpOpen((v) => !v)}
                aria-expanded={helpOpen}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 rounded-md"
              >
                <span>How triggers fire — priority &amp; execution order</span>
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform ${helpOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {helpOpen && (
                <div className="px-4 pb-3 pt-1 text-xs text-slate-600 leading-relaxed space-y-2">
                  <div>
                    <div className="font-semibold text-slate-700">Pre-conversation (fires before a chat exists):</div>
                    <ul className="list-disc ml-5">
                      <li><span className="font-medium">Widget Loads on Page</span> — evaluated at page load. Can hide the widget or swap chat windows.</li>
                      <li><span className="font-medium">Chat Button Clicked</span> — evaluated when visitor clicks. Can drive pre-chat logic.</li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-700">Conversation-scope (fires during a chat):</div>
                    <ul className="list-disc ml-5">
                      <li><span className="font-medium">Conversation Opens</span> — runs once when a new conversation starts. Can route to bot, team, or agent.</li>
                      <li><span className="font-medium">Label Applied</span> — runs when an agent/bot labels the conversation.</li>
                      <li><span className="font-medium">Webhook</span> — runs when an external system calls in.</li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-700">Post-conversation:</div>
                    <ul className="list-disc ml-5">
                      <li><span className="font-medium">Conversation Resolved</span> — runs after the chat ends. Used for surveys, CRM push, reporting.</li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-700">Execution order (always):</div>
                    <ol className="list-decimal ml-5">
                      <li>Priority Rules always fire first (e.g. set-conversation-priority).</li>
                      <li>Exclusive Workflows — first match by routingPriority (lower = higher).</li>
                      <li>Always-run Workflows — fire alongside the exclusive winner if their conditions match.</li>
                      <li>Automation/AI Rules — fire only if NO workflow matched.</li>
                      <li>Team routing — the final fallback.</li>
                    </ol>
                  </div>
                  <p className="text-slate-500">
                    A Workflow takes over the routing for its trigger. If no Workflow matches, Rules with the same
                    concern fire (route-to-team, use-ai, etc.). Changing &ldquo;After this workflow&rdquo; to
                    &ldquo;Hand off to agent&rdquo; forces agent routing regardless of the workflow&rsquo;s internal actions.
                  </p>
                </div>
              )}
            </div>

            <hr className="mb-4 border-slate-200" />

            {/* Category sections */}
            <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-4">
              {CATEGORY_ORDER.map((cat) => {
                const triggers = grouped.get(cat) ?? [];
                if (triggers.length === 0) return null;
                const isOpen = openCategories.has(cat);

                return (
                  <div key={cat}>
                    {/* Category header */}
                    <button
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      className="w-full flex items-center justify-between py-1.5 text-left group"
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? "Collapse" : "Expand"} ${cat} triggers`}
                    >
                      <div>
                        <span className="font-semibold text-slate-700 text-sm">{cat}</span>
                        <span className="ml-2 text-xs text-slate-400">{CATEGORY_DESCRIPTIONS[cat]}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">{triggers.length}</span>
                        <svg
                          className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>

                    {/* Trigger cards */}
                    {isOpen && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                        {triggers.map((trigger) => (
                          <TriggerCard
                            key={trigger.id}
                            trigger={trigger}
                            onClick={() => onSelect(trigger.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {filtered.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-8">
                  No triggers match &ldquo;{query}&rdquo;
                </p>
              )}
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

// ── TriggerCard ───────────────────────────────────────────────────────────────
function TriggerCard({
  trigger,
  onClick,
}: {
  trigger: TriggerDef;
  onClick?: () => void;
}) {
  const isDisabled = !trigger.live && trigger.premium;

  return (
    <div
      role={isDisabled ? undefined : "button"}
      tabIndex={isDisabled ? undefined : 0}
      onClick={isDisabled ? undefined : onClick}
      onKeyDown={isDisabled ? undefined : (e) => e.key === "Enter" && onClick?.()}
      aria-label={isDisabled ? undefined : `Select trigger: ${trigger.title}`}
      className={`group p-4 border rounded-lg flex flex-col gap-2 transition-all ${
        isDisabled
          ? "bg-slate-50 border-slate-200 cursor-not-allowed"
          : "bg-white border-slate-200 hover:border-primary hover:shadow-sm cursor-pointer"
      }`}
    >
      {/* Icon + badge row */}
      <div className="flex items-center justify-between">
        <trigger.icon
          className={`w-6 h-6 ${
            isDisabled
              ? "stroke-gray-300 fill-gray-300 text-slate-300"
              : "stroke-slate-500 fill-slate-500 text-slate-500 group-hover:stroke-primary group-hover:fill-primary group-hover:text-primary"
          }`}
        />
        <div className="flex items-center gap-1">
          {trigger.premium && <PremiumTag />}
        </div>
      </div>

      {/* Title + description */}
      <div>
        <div className={`font-semibold text-sm leading-tight ${
          isDisabled ? "text-slate-500" : "text-slate-800 group-hover:text-primary"
        }`}>
          {trigger.title}
        </div>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed line-clamp-2">
          {trigger.description}
        </p>
      </div>

      {/* Examples — reserve height to prevent bounce */}
      {!isDisabled && (
        <ul className="mt-1 space-y-0.5 min-h-[2.5rem]">
          {trigger.examples.slice(0, 2).map((ex, i) => (
            <li key={i} className="flex items-start gap-1 text-[11px] text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-150">
              <span className="mt-0.5 shrink-0">›</span>
              <span>{ex}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
